import React, { useEffect } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Typography } from "@mui/material"
import { useMutationAppleLogin } from "api/reactQuery/mutations/appleLogin"
import { useMutationGoogleLogin } from "api/reactQuery/mutations/googleLogin"
import { useMutationLoginTeacher } from "api/reactQuery/mutations/teacher"
import { useQueryTeacherCleverLogin } from "api/reactQuery/queries/teacherCleverLogin"
import { AxiosError } from "axios"
import AppleButton from "components/common/appleButton"
import CleverButton from "components/common/button/cleverButton"
import RegisterButtons from "components/common/button/registerButtons"
import CleverSSOLoader from "components/common/cleverSSOLoader"
import GoogleButton from "components/common/googleButton"
import OpenIdSSOLoaders from "components/common/openIdSSOLoaders"
import RegisterInformation from "components/common/registerInformation"
import RegisterInput from "components/common/textField/registerInput"
import { useApple } from "hooks/apple"
import { useCleverSSO } from "hooks/cleverSSO/useCleverSSO"
import { useGoogle } from "hooks/google"
import usePostLoginNavigate from "hooks/postLoginNavigate/usePostLoginNavigate"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch } from "hooks/store"
import { FormProvider, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { setActivePageSignUp, setAuthData } from "store/auth/auth.slice"
import { UserRole } from "ts/enums/User"
import { getErrorMessage } from "utils/api"

import { POLICY, TERMS } from "../signAsTeacher/SignAsTeacher.config"
import useSaveUTMs from "../signAsTeacher/useSaveUTMs"
import { defaultValues, validationSchema } from "./LoginAsTeacher.config"
import { IUserInfo } from "./LoginAsTeacher.types"

const LoginAsTeacher = () => {
  useSaveUTMs()
  const { showSnackbar } = useCustomSnackbar()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const navigateAfterLogin = usePostLoginNavigate()

  const { isCleverSSOLoading, handleCleverReset, handleCleverAuthSuccess } =
    useCleverSSO({ userType: "teacher" })

  useEffect(() => {
    const isStripeEnabled = searchParams.has("isStripe")
    if (isStripeEnabled) {
      sessionStorage.setItem("specialLink", JSON.stringify(isStripeEnabled))
    }
  }, [searchParams])

  const methods = useForm<IUserInfo>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues,
  })

  const { mutate: loginTeacher, isLoading: isLoadingLogin } =
    useMutationLoginTeacher({
      options: {
        onSuccess: (data, variables) => {
          if (data.data.finalization_required) {
            dispatch(
              setAuthData({
                token: data.data.access_token,
                finalized: false,
                role: UserRole.TEACHER,
                is_email_confirmed: data.data.is_email_confirmed,
              }),
            )
            navigate(RoutePath.SIGNUP)
            dispatch(setActivePageSignUp(2))
            return
          }
          showSnackbar({
            title: t("noAuthFlow.loginSuccessful"),
          })
          dispatch(
            setAuthData({
              token: data.data.access_token,
              finalized: true,
              role: UserRole.TEACHER,
              is_email_confirmed: data.data.is_email_confirmed,
            }),
          )

          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: "login",
            user: variables.username,
          })
          if (JSON.parse(localStorage.getItem("isBadAccount") ?? "false")) {
            localStorage.removeItem("isBadAccount")
            queueMicrotask(() => {
              navigate("/settings", { replace: true })
            })
            return
          }
          navigateAfterLogin()
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessage(error),
            variant: "error",
          })
        },
      },
    })

  const handleSSOSuccess = (data: {
    data: { access_token: string; finalization_required: boolean }
  }) => {
    dispatch(
      setAuthData({
        token: data.data.access_token,
        role: UserRole.TEACHER,
        finalized: !data.data.finalization_required,
        is_email_confirmed: true,
      }),
    )
    showSnackbar({
      title: t(
        data.data.finalization_required
          ? "noAuthFlow.emailAndPasswordCreated"
          : "noAuthFlow.loginSuccessful",
      ),
    })
    if (data.data.finalization_required) {
      navigate(RoutePath.SIGNUP)
      dispatch(setActivePageSignUp(2))
    } else {
      navigateAfterLogin()
    }
  }

  const handleSSOError = (error: AxiosError) => {
    showSnackbar({
      title: getErrorMessage(error),
      variant: "error",
    })
  }

  const { mutate: googleMutate } = useMutationGoogleLogin({
    option: {
      onSuccess: handleSSOSuccess,
      onError: handleSSOError,
    },
  })

  const { refButton } = useGoogle({ mutate: googleMutate })

  const { mutate: appleMutate } = useMutationAppleLogin({
    option: {
      onSuccess: handleSSOSuccess,
      onError: handleSSOError,
    },
  })

  const { signIn: appleSignIn } = useApple({ mutate: appleMutate })

  const { refetch: cleverLogin } = useQueryTeacherCleverLogin()

  const onSubmit = (data: IUserInfo) => {
    loginTeacher({
      password: data.password,
      username: data.username,
    })
  }

  return (
    <>
      <CleverSSOLoader
        isLoading={isCleverSSOLoading}
        userType="teacher"
        onReset={handleCleverReset}
        onAuthSuccess={handleCleverAuthSuccess}
      />
      <OpenIdSSOLoaders userType="teacher" />
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Box textAlign="left">
            <Typography
              variant="h2"
              component="p"
              color="primary.main"
              fontWeight="600"
            >
              {t("noAuthFlow.teacher")}
            </Typography>
            <Typography
              component="h1"
              variant="body1"
              fontSize="24px"
              fontWeight="500"
              color="mockup.neutral50"
              sx={{ pt: 1, pb: 4 }}
            >
              {t("noAuthFlow.signIn")}
            </Typography>
          </Box>
          <GoogleButton text={t("noAuthFlow.signInGoogle")} ref={refButton} />
          <Box sx={{ mt: 2 }}>
            <AppleButton
              text={t("noAuthFlow.signInApple")}
              onClick={appleSignIn}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <CleverButton
              text="Sign in with Clever"
              onClick={() => {
                cleverLogin()
              }}
            />
          </Box>
          <Typography variant="subtitle1" color="mockup.grey80" py={2}>
            {t("defaultTranslations.or")}
          </Typography>
          <Box>
            <RegisterInput name="username" type="email" />
            <RegisterInformation isResetPassword />
            {process.env.REACT_APP_TYPE === "bankeeonline" && (
              <Box mt={2}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    fontWeight: "500",
                    color: theme.palette.mockup.neutral0,
                    textAlign: "left",
                  })}
                >
                  <Trans
                    i18nKey="noAuthFlow.bySigningUpYouAgreeToClassBanksTermsOfServiceAndPrivacyPolicy"
                    components={{
                      Link1: TERMS,
                      Link2: POLICY,
                    }}
                  />
                </Typography>
              </Box>
            )}
            <RegisterButtons
              isLoading={isLoadingLogin}
              submitButton={t("noAuthFlow.signIn")}
              onClick={() =>
                (window.location.href = "https://www.classbank.com/")
              }
            />
            <RegisterInformation isSignInOrUp />
          </Box>
        </FormProvider>
      </form>
    </>
  )
}

export default LoginAsTeacher
